import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { sendContactMessage } from '../api/client';
import { ApiClientError } from '../api/types';
import '../components/ui/ui-button';
import { formatPhone, phoneTelHref, PUBLIC_PHONE, CONTACT_ADDRESS_LINE, CONTACT_EMAIL, CONTACT_HOURS_CLOSED, CONTACT_HOURS_OPEN, CONTACT_MAPS_URL } from '../content/services';
import { trackConversion } from '../lib/analytics';
import { alertStyles, boxSizingStyles, fullBleedStyles } from '../styles/lit-shared';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

@customElement('contact-page')
export class ContactPage extends LitElement {
  @state() private name = '';
  @state() private email = '';
  @state() private message = '';
  @state() private website = '';
  @state() private status: FormStatus = 'idle';
  @state() private errorMessage = '';

  private _revealObserver: IntersectionObserver | null = null;

  firstUpdated() {
    this._setupReveal();
  }

  disconnectedCallback() {
    this._revealObserver?.disconnect();
    this._revealObserver = null;
    super.disconnectedCallback();
  }

  private _setupReveal() {
    const root = this.shadowRoot;
    if (!root) return;

    const els = root.querySelectorAll('.reveal');
    if (!els.length) return;

    if (typeof IntersectionObserver === 'undefined') {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    this._revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this._revealObserver?.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );

    els.forEach((el) => this._revealObserver!.observe(el));
  }

  static styles = [
    boxSizingStyles,
    fullBleedStyles,
    alertStyles,
    css`
      :host {
        display: block;
        width: 100%;
      }

      /* —— Hero —— */
      .hero {
        margin-top: calc(-1 * var(--space-xl));
        margin-bottom: var(--space-md);
        padding: var(--space-2xl) var(--space-lg);
        overflow: hidden;
        background:
          radial-gradient(ellipse 70% 60% at 20% 40%, var(--glow-violet), transparent 55%),
          radial-gradient(ellipse 50% 50% at 85% 25%, var(--glow-cyan), transparent 50%),
          radial-gradient(ellipse 40% 100% at 0% 50%, var(--glow-violet), transparent 60%),
          radial-gradient(ellipse 40% 100% at 100% 50%, var(--glow-cyan), transparent 60%),
          var(--color-bg);
      }

      .hero-orb {
        position: absolute;
        border-radius: 50%;
        filter: blur(60px);
        pointer-events: none;
        animation: orb-drift 16s var(--ease-out) infinite alternate;
      }

      .hero-orb--1 {
        top: 5%;
        right: 8%;
        width: min(300px, 42vw);
        height: min(300px, 42vw);
        background: var(--glow-cyan);
      }

      .hero-orb--2 {
        bottom: -15%;
        left: 15%;
        width: min(240px, 38vw);
        height: min(240px, 38vw);
        background: var(--glow-fuchsia);
        animation-delay: -6s;
      }

      @keyframes orb-drift {
        from {
          transform: translate(0, 0) scale(1);
        }
        to {
          transform: translate(16px, -12px) scale(1.06);
        }
      }

      .hero-inner {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: var(--max-width);
        margin: 0 auto;
      }

      .section-label {
        display: block;
        margin-bottom: var(--space-md);
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: var(--color-accent);
      }

      h1 {
        margin: 0 0 var(--space-md);
        font-size: clamp(2.25rem, 6vw, 3.5rem);
        font-weight: 700;
        letter-spacing: -0.04em;
        line-height: 1.1;
        color: var(--color-text);
      }

      h1 .title-grad {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .lede {
        margin: 0 0 var(--space-lg);
        max-width: 48ch;
        font-size: 1.0625rem;
        line-height: 1.65;
        color: var(--color-text-muted);
      }

      .contact-card {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0;
        margin: 0;
        padding: 0;
        max-width: 52rem;
        font-style: normal;
        border: 1px solid var(--color-border);
        border-radius: var(--radius-lg);
        background: var(--color-surface);
        overflow: hidden;
      }

      .contact-row {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
        min-width: 0;
        padding: 0.9rem 1rem;
        color: var(--color-text);
        text-decoration: none;
        border-top: 1px solid var(--color-border);
      }

      .contact-row:first-child {
        border-top: none;
      }

      a.contact-row:hover {
        background: rgb(255 255 255 / 4%);
      }

      .contact-k {
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--color-accent);
      }

      .contact-v {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        min-width: 0;
        font-weight: 600;
        line-height: 1.4;
        overflow-wrap: anywhere;
      }

      .contact-v img {
        width: 1.125rem;
        height: 1.125rem;
        flex-shrink: 0;
      }

      .contact-row--hours .contact-v {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.15rem;
      }

      .contact-closed {
        color: var(--color-text-muted);
        font-weight: 500;
      }

      @media (min-width: 700px) {
        .contact-card {
          grid-template-columns: 0.8fr 1.1fr 1.3fr;
        }

        .contact-row {
          border-top: none;
          border-left: 1px solid var(--color-border);
        }

        .contact-row:first-child {
          border-left: none;
        }

        .contact-row--hours {
          grid-column: 1 / -1;
          border-left: none;
          border-top: 1px solid var(--color-border);
        }

        .contact-row--hours .contact-v {
          flex-direction: row;
          flex-wrap: wrap;
          gap: 0.2rem 1.25rem;
        }
      }

      /* —— Form band —— */
      .band {
        padding: var(--space-2xl) var(--space-lg) var(--space-2xl);
        overflow: hidden;
        background:
          radial-gradient(ellipse 60% 70% at 0% 50%, var(--glow-violet), transparent 55%),
          radial-gradient(ellipse 45% 55% at 100% 80%, var(--glow-cyan), transparent 50%),
          radial-gradient(ellipse 40% 100% at 0% 50%, var(--glow-violet), transparent 60%),
          radial-gradient(ellipse 40% 100% at 100% 50%, var(--glow-cyan), transparent 60%),
          var(--color-bg);
      }

      .band-inner {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: 40rem;
        margin: 0 auto;
        padding-left: var(--space-xl);
        border-left: 2px solid transparent;
        border-image: linear-gradient(
            to bottom,
            var(--color-accent),
            var(--color-primary-light),
            transparent
          )
          1;
      }

      .form-header {
        margin-bottom: var(--space-xl);
      }

      .form-header .section-title {
        font-size: clamp(1.5rem, 3vw, 2rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        margin: 0 0 var(--space-sm);
        color: var(--color-text);
      }

      .form-header p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.975rem;
        line-height: 1.55;
      }

      form {
        display: flex;
        flex-direction: column;
        gap: var(--space-md);
      }

      .field {
        display: flex;
        flex-direction: column;
        gap: var(--space-xs);
      }

      label {
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-text-muted);
      }

      input,
      textarea {
        width: 100%;
        padding: 0.875rem 1rem;
        border-radius: var(--radius-sm);
        border: 1px solid var(--color-border-strong);
        background: rgb(255 255 255 / 3%);
        color: var(--color-text);
        font-family: var(--font-sans);
        font-size: 0.95rem;
        line-height: 1.5;
        outline: none;
        transition:
          border-color var(--duration-normal) var(--ease-out),
          box-shadow var(--duration-normal) var(--ease-out),
          background var(--duration-normal) var(--ease-out);
      }

      input:focus,
      textarea:focus {
        border-color: var(--color-accent);
        background: rgb(255 255 255 / 5%);
        box-shadow: 0 0 0 2px rgb(34 211 238 / 18%);
      }

      input::placeholder,
      textarea::placeholder {
        color: var(--color-text-subtle);
      }

      textarea {
        min-height: 10rem;
        resize: vertical;
      }

      .hp {
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        overflow: hidden;
        opacity: 0;
        pointer-events: none;
      }

      .actions {
        margin-top: var(--space-sm);
      }

      .feedback {
        margin-top: var(--space-md);
      }

      .success-block {
        padding: var(--space-lg) 0;
      }

      .success-block .section-title {
        font-size: clamp(1.5rem, 3vw, 2rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        margin: 0 0 var(--space-md);
        color: var(--color-text);
      }

      .success-block .section-title .title-grad {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .reveal {
        opacity: 0;
        transform: translateY(18px);
        transition:
          opacity 700ms var(--ease-out),
          transform 700ms var(--ease-out);
      }

      .reveal.is-visible {
        opacity: 1;
        transform: translateY(0);
      }

      .reveal-delay-1 {
        transition-delay: 80ms;
      }

      @media (max-width: 639px) {
        .hero {
          margin-top: calc(-1 * var(--space-lg));
          padding: var(--space-2xl) var(--space-md);
        }

        .band {
          padding: var(--space-2xl) var(--space-md);
        }

        .band-inner {
          padding-left: var(--space-lg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .hero-orb {
          animation: none;
        }

        .reveal {
          opacity: 1;
          transform: none;
          transition: none;
        }
      }
    `,
  ];

  private handleInput(e: Event) {
    const el = e.target as HTMLInputElement | HTMLTextAreaElement;
    const { name, value } = el;
    if (name === 'name') this.name = value;
    else if (name === 'email') this.email = value;
    else if (name === 'message') this.message = value;
    else if (name === 'website') this.website = value;
  }

  private async handleSubmit(e: Event) {
    e.preventDefault();
    if (this.status === 'submitting') return;

    this.status = 'submitting';
    this.errorMessage = '';

    try {
      await sendContactMessage({
        name: this.name.trim(),
        email: this.email.trim(),
        message: this.message.trim(),
        website: this.website,
      });
      this.status = 'success';
      this.name = '';
      this.email = '';
      this.message = '';
      this.website = '';
    } catch (err) {
      this.status = 'error';
      this.errorMessage =
        err instanceof ApiClientError
          ? err.message
          : 'No se pudo enviar el mensaje. Inténtalo más tarde.';
    }
  }

  render() {
    const submitting = this.status === 'submitting';

    return html`
      <section class="hero full-bleed" aria-label="Contacto">
        <span class="hero-orb hero-orb--1" aria-hidden="true"></span>
        <span class="hero-orb hero-orb--2" aria-hidden="true"></span>
        <div class="hero-inner">
          <span class="section-label">Contacto</span>
          <h1>Cuéntame tu <span class="title-grad">idea</span></h1>
          <p class="lede">
            Si quieres una web, una automatización, una integración o contrastar si tu idea
            tiene sentido — llámame o escríbeme. Empiezo escuchando; el código viene después.
          </p>
          <address class="contact-card">
            <a
              class="contact-row"
              href=${phoneTelHref(PUBLIC_PHONE)}
              @click=${() => trackConversion('phone')}
            >
              <span class="contact-k">Teléfono</span>
              <span class="contact-v">
                <img src="/whatsapp_icon.svg" alt="" width="18" height="18" />
                ${formatPhone(PUBLIC_PHONE)}
              </span>
            </a>
            <a
              class="contact-row"
              href=${`mailto:${CONTACT_EMAIL}`}
              @click=${() => trackConversion('email')}
            >
              <span class="contact-k">Email</span>
              <span class="contact-v">${CONTACT_EMAIL}</span>
            </a>
            <a class="contact-row" href=${CONTACT_MAPS_URL} target="_blank" rel="noopener noreferrer">
              <span class="contact-k">Dirección</span>
              <span class="contact-v">${CONTACT_ADDRESS_LINE}</span>
            </a>
            <div class="contact-row contact-row--hours">
              <span class="contact-k">Horario</span>
              <span class="contact-v">
                <span>${CONTACT_HOURS_OPEN}</span>
                <span class="contact-closed">${CONTACT_HOURS_CLOSED}</span>
              </span>
            </div>
          </address>
        </div>
      </section>

      <section class="band full-bleed" aria-label="Formulario de contacto">
        <div class="band-inner reveal">
          ${this.status === 'success'
            ? html`
                <div class="success-block" role="status">
                  <span class="section-label">Enviado</span>
                  <h2 class="section-title">
                    Lo he <span class="title-grad">recibido</span>
                  </h2>
                  <div class="alert alert--success">
                    Te contesto al email que me has dejado. Si encaja, el siguiente paso suele
                    ser una llamada de hoja de ruta.
                  </div>
                </div>
              `
            : html`
                <div class="form-header">
                  <span class="section-label">01 · Mensaje</span>
                  <h2 class="section-title">Sin rodeos</h2>
                  <p>
                    Nombre, email y el contexto: qué quieres construir, para quién y en qué
                    punto estás. Con eso basta para empezar.
                  </p>
                </div>

                <form @submit=${this.handleSubmit} novalidate>
                  <div class="field">
                    <label for="contact-name">Nombre</label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      autocomplete="name"
                      maxlength="100"
                      required
                      placeholder="Cómo te llamas"
                      .value=${this.name}
                      @input=${this.handleInput}
                    />
                  </div>

                  <div class="field">
                    <label for="contact-email">Email</label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      autocomplete="email"
                      maxlength="254"
                      required
                      placeholder="tu@email.com"
                      .value=${this.email}
                      @input=${this.handleInput}
                    />
                  </div>

                  <div class="field">
                    <label for="contact-message">Mensaje</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      maxlength="5000"
                      required
                      placeholder="La idea, el problema que resuelve y en qué puedo ayudarte…"
                      .value=${this.message}
                      @input=${this.handleInput}
                    ></textarea>
                  </div>

                  <div class="hp" aria-hidden="true">
                    <label for="contact-website">Website</label>
                    <input
                      id="contact-website"
                      type="text"
                      name="website"
                      tabindex="-1"
                      autocomplete="off"
                      .value=${this.website}
                      @input=${this.handleInput}
                    />
                  </div>

                  <div class="actions">
                    <ui-button type="submit" variant="primary" fullwidth ?disabled=${submitting}>
                      ${submitting ? 'Enviando…' : 'Enviar mensaje'}
                    </ui-button>
                  </div>

                  ${this.status === 'error'
                    ? html`
                        <div class="alert alert--error feedback" role="alert">
                          ${this.errorMessage}
                        </div>
                      `
                    : ''}
                </form>
              `}
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'contact-page': ContactPage;
  }
}
